/**
 * Enhanced Product – Cart & App-Only Pricing
 *
 * Handles add-to-cart logic, button loading states, and app-only mode pricing.
 *
 * Depends on (set by enhanced-product-information.liquid inline script):
 *   window.enhancedIsProductPage  — boolean
 *   window.enhancedProductData    — product/pricing data object (null on non-product pages)
 *   window.selectedVariantId      — currently-selected variant ID
 *   window.buttonTranslations     — locale → translation map (from enhanced-product-helpers.js)
 *   window.customerData           — customer session info
 *   window.addToCart              — unified add-to-cart (src/scripts/add-to-cart.ts)
 *   window.openSignupModal        — sign-up modal opener
 *   getCurrentLocale()            — locale helper (from enhanced-product-helpers.js)
 */

// ---------------------------------------------------------------------------
// Button loading state helpers
// ---------------------------------------------------------------------------

function showButtonLoading(button) {
  button.disabled = true;
  const spinner = button.querySelector('.button-spinner');
  const content = button.querySelector('.button-content');
  if (spinner) spinner.style.display = 'inline-flex';
  if (content) content.style.display = 'none';
}

function hideButtonLoading(button) {
  button.disabled = false;
  const spinner = button.querySelector('.button-spinner');
  const content = button.querySelector('.button-content');
  if (spinner) spinner.style.display = 'none';
  if (content) content.style.display = 'inline';
}

// ---------------------------------------------------------------------------
// Add-to-cart handler
// ---------------------------------------------------------------------------

function setupCustomAddToCartHandlers() {
  const enhancedSection = document.querySelector('.enhanced-product-section');
  if (!enhancedSection) return;

  const dataAddToCartBtn = enhancedSection.querySelector('[data-product-add-to-cart]');
  if (!dataAddToCartBtn) return;

  dataAddToCartBtn.addEventListener('click', function (e) {
    e.preventDefault();

    // Hide any existing error banner
    const errorBanner = enhancedSection.querySelector('[data-add-to-cart-error]');
    if (errorBanner) errorBanner.style.display = 'none';

    const showError = () => {
      if (errorBanner) errorBanner.style.display = 'flex';
    };

    const quantity = parseInt(enhancedSection.querySelector('#quantity')?.value, 10) || 1;
    const isSubscription = !!enhancedSection.querySelector('#subscribe')?.checked;
    const deliverySelect = enhancedSection.querySelector('#delivery-select');
    const selectedOption = deliverySelect?.options[deliverySelect.selectedIndex];
    let sellingPlanId = selectedOption?.getAttribute('data-selling-plan-id') || selectedOption?.value || null;

    const isAppOnlyMode = enhancedSection.getAttribute('data-app-only-mode') === 'true';
    const isLoggedIn = window.customerData?.logged_in === true;
    const pd = window.enhancedProductData;

    // Resolve the variant to add.
    let variantId = window.selectedVariantId || pd?.defaultVariantId || null;
    let properties = {};
    let appendApp = true;

    if (isAppOnlyMode) {
      // The item being added IS the app product — flag it and don't attach a 2nd app.
      properties = { _app_only: 'true' };
      appendApp = false;
    } else if (pd && pd.hasSellingPlans && isSubscription && !sellingPlanId) {
      // Subscription chosen but no plan picked yet — fall back to the default plan.
      sellingPlanId = pd.defaultSellingPlanId || null;
    }

    if (!variantId || typeof window.addToCart !== 'function') {
      showError();
      return;
    }

    // The shared circular spinner (button-spinner / button-content swap) covers the
    // whole request. window.addToCart appends the free app item + opens the drawer.
    const doAdd = () => {
      showButtonLoading(dataAddToCartBtn);
      window.addToCart({
        items: [{
          id: variantId,
          quantity: quantity,
          sellingPlanId: isSubscription ? sellingPlanId : null,
          properties: properties,
        }],
        subscription: isSubscription,
        appendApp: appendApp,
        source: 'enhanced-product',
      })
        .catch(() => showError())
        .finally(() => hideButtonLoading(dataAddToCartBtn));
    };

    // App-only mode: gate behind sign-up if not logged in, then add.
    if (isAppOnlyMode && !isLoggedIn) {
      if (typeof window.openSignupModal === 'function') {
        window.openSignupModal(() => doAdd());
      }
      return;
    }

    doAdd();
  });
}

// ---------------------------------------------------------------------------
// App-only mode pricing adjustments
// ---------------------------------------------------------------------------

function checkAndAdjustAppOnlyPricing() {
  const enhancedSection = document.querySelector('.enhanced-product-section');
  if (!enhancedSection) return;
  if (enhancedSection.getAttribute('data-app-only-mode') !== 'true') return;

  const currentLocale = typeof getCurrentLocale === 'function' ? getCurrentLocale() : 'en-US';
  const langCode = currentLocale.split('-')[0];
  const translations = window.buttonTranslations || {};
  const trans =
    translations[currentLocale] ||
    Object.values(translations).find((_, i) =>
      Object.keys(translations)[i].startsWith(langCode + '-')
    ) ||
    translations['en-US'] ||
    {};

  const subscriptionTitle = enhancedSection.querySelector('.subscription-option.subscribe-save h3');
  if (subscriptionTitle) {
    subscriptionTitle.textContent = trans.getTheApp || 'Get the app';
  }

  // Hide the crossed-out original price in app-only mode
  const originalPriceEl = enhancedSection.querySelector(
    '.subscription-option.subscribe-save .original-price'
  );
  if (originalPriceEl) originalPriceEl.style.display = 'none';

  // Update button leading text node without re-rendering the price span
  const subscribeTextEl = enhancedSection.querySelector('.subscribe-text');
  if (subscribeTextEl) {
    const cartPriceEl = subscribeTextEl.querySelector('.cart-price');
    if (cartPriceEl) {
      const textNode = Array.from(subscribeTextEl.childNodes).find(
        (n) => n.nodeType === Node.TEXT_NODE
      );
      const newText = `${trans.subscribeNow || 'Get it now'} - `;
      if (textNode) {
        textNode.nodeValue = newText;
      } else {
        subscribeTextEl.insertBefore(document.createTextNode(newText), cartPriceEl);
      }
    }
  }
}

function observeAppOnlyMode() {
  const enhancedSection = document.querySelector('.enhanced-product-section');
  if (!enhancedSection) return;

  checkAndAdjustAppOnlyPricing();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'data-app-only-mode'
      ) {
        checkAndAdjustAppOnlyPricing();
      }
    });
  });

  observer.observe(enhancedSection, {
    attributes: true,
    attributeFilter: ['data-app-only-mode'],
  });
}

// Initialize after a short delay to ensure DMO scripts have run
setTimeout(() => observeAppOnlyMode(), 500);
